export const legalEs = {
  backToHome: 'Volver al inicio',
  lastUpdated: 'Última actualización: {{date}}',
  privacy: {
    title: 'Política de Privacidad',
    intro:
      'Esta Política de Privacidad describe cómo Customer Lab («nosotros») recopila, usa y protege tu información cuando utilizas la plataforma operativa centrada en IA.',
    dataCollected: {
      title: 'Información que recopilamos',
      account: 'Datos de cuenta: nombre, correo electrónico y contraseña cifrada al registrarte o aceptar una invitación.',
      tenant:
        'Datos de empresa: nombre de la organización, configuración de roles (owner, admin, agente) y preferencias asociadas a tu espacio de trabajo.',
      operational:
        'Datos operativos que cargas o importas: clientes, ubicaciones, documentos fiscales, comunicaciones, adjuntos, importaciones desde Excel/CSV y metadatos relacionados.',
      ai: 'Datos del asistente de IA: historial de conversaciones, consultas y respuestas vinculadas a tu empresa, dentro de los límites de las herramientas habilitadas.'
    },
    howWeUse: {
      title: 'Cómo usamos tu información',
      item1: 'Para operar la plataforma: autenticación, aislamiento por empresa, permisos y trazabilidad básica.',
      item2:
        'Para que el asistente de IA responda con contexto real de tu operación (clientes, facturas, documentos), sin sustituir tus registros por datos inventados.',
      item3: 'Para procesar importaciones, validaciones y flujos de documentos fiscales que gestionas en la app.',
      item4: 'Para enviar correos transaccionales (invitaciones, restablecimiento de contraseña, notificaciones operativas).',
      item5: 'Para monitorear la salud del servicio, prevenir abusos y mejorar la estabilidad de la plataforma.'
    },
    thirdParty: {
      title: 'Servicios de terceros',
      intro: 'Utilizamos proveedores externos para operar Customer Lab:',
      openai:
        'Proveedores de IA (p. ej. OpenAI) — las consultas del asistente y el contexto permitido por las herramientas pueden enviarse a sus APIs según la configuración activa.',
      google:
        'Google — inicio de sesión OAuth cuando eliges esa opción, dentro de la infraestructura de autenticación del grupo de productos.',
      smtp: 'Servicios de correo (SMTP) — para notificaciones transaccionales.',
      invoicing:
        'Proveedores de facturación electrónica — cuando un conector esté disponible y lo habilites, podremos sincronizar comprobantes y estados según la integración acordada. No implica que todas las integraciones estén activas por defecto.'
    },
    dataSecurity: {
      title: 'Seguridad de los datos',
      text: 'Aplicamos medidas estándar de la industria: HTTPS, tokens de acceso, aislamiento lógico por empresa, controles de acceso por rol y almacenamiento protegido de credenciales sensibles. Ningún método de transmisión o almacenamiento es 100 % infalible.'
    },
    retention: {
      title: 'Retención de datos',
      text: 'Conservamos tus datos mientras tu cuenta y empresa estén activas o según obligaciones legales aplicables. Puedes solicitar la eliminación de tu cuenta y datos asociados contactándonos por los medios indicados abajo.'
    },
    rights: {
      title: 'Tus derechos',
      text: 'Puedes acceder, corregir o solicitar la eliminación de tus datos personales. Los administradores de tu empresa gestionan usuarios y permisos dentro de la organización. Para ejercer derechos sobre tu cuenta individual, contáctanos al correo de contacto.'
    },
    changes: {
      title: 'Cambios a esta política',
      text: 'Podemos actualizar esta política periódicamente. Publicaremos la versión vigente en esta página y, ante cambios relevantes, notificaremos a usuarios registrados por correo cuando corresponda.'
    },
    contact: {
      title: 'Contacto',
      text: 'Consultas sobre privacidad: info@customer-lab.com.'
    }
  },
  terms: {
    title: 'Términos de Servicio',
    intro:
      'Estos Términos de Servicio («Términos») regulan el uso de Customer Lab. Al crear una cuenta, aceptar una invitación o usar la plataforma, aceptas estos Términos.',
    acceptance: {
      title: 'Aceptación de los términos',
      text: 'Si no estás de acuerdo con estos Términos, no puedes usar Customer Lab.'
    },
    description: {
      title: 'Descripción del servicio',
      text: 'Customer Lab es una plataforma operativa centrada en IA para gestionar clientes, documentos fiscales, comunicaciones, adjuntos e importaciones. El asistente de IA consulta datos reales de tu empresa (herramientas de solo lectura donde corresponda) y te ayuda a navegar la operación. La arquitectura está preparada para integraciones de facturación electrónica según disponibilidad; no todas las integraciones están activas desde el primer día.'
    },
    accounts: {
      title: 'Cuentas y empresas',
      item1: 'Debes proporcionar información veraz al registrarte o completar una invitación.',
      item2: 'Eres responsable de la seguridad de tus credenciales y del uso de tu cuenta.',
      item3: 'Cada empresa opera en un espacio aislado; los administradores gestionan usuarios y roles dentro de su organización.'
    },
    content: {
      title: 'Propiedad del contenido',
      text: 'Tú y tu organización conservan la propiedad de los datos operativos que cargan (clientes, facturas, comunicaciones, archivos). Customer Lab no reclama propiedad sobre ese contenido; te proporciona herramientas para gestionarlo y consultarlo.'
    },
    ai: {
      title: 'Asistente de IA',
      item1:
        'Las respuestas se generan con servicios de IA de terceros y datos de tu empresa. No garantizamos exactitud absoluta: revisa montos, estados e IDs antes de decisiones críticas.',
      item2:
        'El asistente está diseñado para citar registros reales y no inventar identificadores ni importes; aun así, la revisión humana sigue siendo tu responsabilidad.',
      item3: 'Las herramientas de consulta están acotadas por empresa y permisos; no accedemos a datos de otras empresas en la plataforma.'
    },
    prohibited: {
      title: 'Uso prohibido',
      item1: 'Usar la plataforma para actividades ilegales, fraudulentas o que vulneren derechos de terceros.',
      item2: 'Intentar eludir el aislamiento entre empresas, explotar vulnerabilidades o interferir con el servicio.',
      item3: 'Cargar malware, realizar ingeniería inversa no autorizada o automatizar abusos contra la API.',
      item4: 'Revender acceso o compartir credenciales fuera de los usuarios autorizados por tu administrador.'
    },
    termination: {
      title: 'Suspensión y baja',
      text: 'Podemos suspender o dar de baja cuentas que violen estos Términos. Tu organización o pueden solicitar la baja escribiendo a info@customer-lab.com.'
    },
    liability: {
      title: 'Limitación de responsabilidad',
      text: 'Customer Lab se ofrece «tal cual». No somos responsables por daños indirectos, pérdida de datos por causas fuera de nuestro control razonable, errores en respuestas de IA o indisponibilidad de integraciones de terceros aún no desplegadas.'
    },
    changes: {
      title: 'Cambios a estos términos',
      text: 'Podemos actualizar estos Términos. El uso continuado tras la publicación de cambios implica aceptación, salvo que la ley exija otro procedimiento.'
    },
    contact: {
      title: 'Contacto',
      text: 'Consultas sobre estos Términos: info@customer-lab.com.'
    }
  }
};

export const legalEn = {
  backToHome: 'Back to home',
  lastUpdated: 'Last updated: {{date}}',
  privacy: {
    title: 'Privacy Policy',
    intro:
      'This Privacy Policy describes how Customer Lab ("we", "us") collects, uses, and protects your information when you use our AI-first operations platform.',
    dataCollected: {
      title: 'Information we collect',
      account: 'Account data: name, email address, and encrypted password when you register or accept an invitation.',
      tenant: 'Company (tenant) data: organization name, role configuration (owner, admin, agent), and workspace settings.',
      operational:
        'Operational data you upload or import: clients, locations, fiscal documents, communications, attachments, Excel/CSV imports, and related metadata.',
      ai: 'AI assistant data: conversation history, queries, and answers scoped to your tenant within enabled tools.'
    },
    howWeUse: {
      title: 'How we use your information',
      item1: 'To operate the platform: authentication, tenant isolation, permissions, and basic audit trails.',
      item2:
        'So the AI assistant can answer with real context from your operation (clients, invoices, documents) without substituting invented records.',
      item3: 'To process imports, validations, and fiscal document workflows you manage in the app.',
      item4: 'To send transactional email (invitations, password reset, operational notifications).',
      item5: 'To monitor service health, prevent abuse, and improve platform stability.'
    },
    thirdParty: {
      title: 'Third-party services',
      intro: 'We use external providers to run Customer Lab:',
      openai:
        'AI providers (e.g. OpenAI) — assistant queries and tool-permitted context may be sent to their APIs per active configuration.',
      google: 'Google — OAuth sign-in when you choose that option, within the product group authentication infrastructure.',
      smtp: 'Email (SMTP) — for transactional notifications.',
      invoicing:
        'Electronic invoicing providers — when a connector is available and you enable it, we may sync vouchers and statuses per the agreed integration. Not all integrations are active by default.'
    },
    dataSecurity: {
      title: 'Data security',
      text: 'We apply industry-standard measures: HTTPS, access tokens, logical tenant isolation, role-based access, and protected storage for sensitive credentials. No transmission or storage method is 100% secure.'
    },
    retention: {
      title: 'Data retention',
      text: 'We retain your data while your account and tenant are active or as required by applicable law. You may request deletion of your account and associated data using the contact details below.'
    },
    rights: {
      title: 'Your rights',
      text: 'You may access, correct, or request deletion of your personal data. Your organization administrators manage users and permissions within the tenant. For individual account rights, contact us at the email below.'
    },
    changes: {
      title: 'Changes to this policy',
      text: 'We may update this policy from time to time. We will post the current version on this page and, for material changes, notify registered users by email when appropriate.'
    },
    contact: {
      title: 'Contact',
      text: 'Privacy questions: info@customer-lab.com.'
    }
  },
  terms: {
    title: 'Terms of Service',
    intro:
      'These Terms of Service ("Terms") govern your use of Customer Lab. By creating an account, accepting an invitation, or using the platform, you agree to these Terms.',
    acceptance: {
      title: 'Acceptance of terms',
      text: 'If you do not agree to these Terms, you may not use Customer Lab.'
    },
    description: {
      title: 'Service description',
      text: 'Customer Lab is an AI-first operations platform for clients, fiscal documents, communications, attachments, and imports. The AI assistant queries real tenant data (read-only tools where applicable) and helps you navigate daily work. The architecture is ready for electronic invoicing integrations as they become available; not every integration is live on day one.'
    },
    accounts: {
      title: 'Accounts and tenants',
      item1: 'You must provide accurate information when registering or completing an invitation.',
      item2: 'You are responsible for securing your credentials and for activity under your account.',
      item3: 'Each company operates in an isolated tenant; administrators manage users and roles within their organization.'
    },
    content: {
      title: 'Content ownership',
      text: 'You and your organization retain ownership of operational data you upload (clients, invoices, communications, files). Customer Lab does not claim ownership of that content; we provide tools to manage and query it.'
    },
    ai: {
      title: 'AI assistant',
      item1:
        'Answers are produced using third-party AI services and your tenant data. We do not guarantee perfect accuracy — review amounts, statuses, and IDs before critical decisions.',
      item2:
        'The assistant is designed to cite real records and not invent identifiers or amounts; human review remains your responsibility.',
      item3: "Query tools are scoped by tenant and permissions; we do not access other companies' data on the platform."
    },
    prohibited: {
      title: 'Prohibited use',
      item1: 'Using the platform for illegal, fraudulent activity or violations of third-party rights.',
      item2: 'Attempting to bypass tenant isolation, exploit vulnerabilities, or disrupt the service.',
      item3: 'Uploading malware, unauthorized reverse engineering, or abusive automation against the API.',
      item4: 'Reselling access or sharing credentials outside users authorized by your administrator.'
    },
    termination: {
      title: 'Suspension and termination',
      text: 'We may suspend or terminate accounts that violate these Terms. Your organization or you may request closure by writing to info@customer-lab.com.'
    },
    liability: {
      title: 'Limitation of liability',
      text: 'Customer Lab is provided "as is". We are not liable for indirect damages, data loss beyond reasonable control, AI response errors, or unavailability of third-party integrations not yet deployed.'
    },
    changes: {
      title: 'Changes to these terms',
      text: 'We may update these Terms. Continued use after publication constitutes acceptance unless applicable law requires otherwise.'
    },
    contact: {
      title: 'Contact',
      text: 'Questions about these Terms: info@customer-lab.com.'
    }
  }
};

export const legalPt = {
  backToHome: 'Voltar ao início',
  lastUpdated: 'Última atualização: {{date}}',
  privacy: {
    title: 'Política de Privacidade',
    intro:
      'Esta Política de Privacidade descreve como o Customer Lab («nós») recolhe, utiliza e protege a sua informação quando utiliza a plataforma operativa centrada em IA.',
    dataCollected: {
      title: 'Informação que recolhemos',
      account: 'Dados de conta: nome, email e palavra-passe encriptada ao registar-se ou aceitar um convite.',
      tenant:
        'Dados da empresa (tenant): nome da organização, configuração de funções (owner, admin, agente) e preferências do espaço de trabalho.',
      operational:
        'Dados operacionais que carrega ou importa: clientes, localizações, documentos fiscais, comunicações, anexos, importações Excel/CSV e metadados relacionados.',
      ai: 'Dados do assistente de IA: histórico de conversas, consultas e respostas limitadas ao seu tenant nas ferramentas ativas.'
    },
    howWeUse: {
      title: 'Como utilizamos a sua informação',
      item1: 'Para operar a plataforma: autenticação, isolamento por tenant, permissões e trilho de auditoria básico.',
      item2:
        'Para o assistente de IA responder com contexto real da operação (clientes, faturas, documentos), sem substituir registos por dados inventados.',
      item3: 'Para processar importações, validações e fluxos de documentos fiscais na aplicação.',
      item4: 'Para enviar email transacional (convites, reposição de palavra-passe, notificações operacionais).',
      item5: 'Para monitorizar a saúde do serviço, prevenir abusos e melhorar a estabilidade.'
    },
    thirdParty: {
      title: 'Serviços de terceiros',
      intro: 'Utilizamos fornecedores externos para operar o Customer Lab:',
      openai:
        'Fornecedores de IA (ex.: OpenAI) — consultas do assistente e contexto permitido pelas ferramentas podem ser enviados às respetivas APIs conforme a configuração ativa.',
      google: 'Google — início de sessão OAuth quando escolhe essa opção, na infraestrutura de autenticação do grupo de produtos.',
      smtp: 'Email (SMTP) — para notificações transacionais.',
      invoicing:
        'Fornecedores de faturação eletrónica — quando um conector estiver disponível e o ativar, podemos sincronizar comprovativos e estados conforme a integração acordada. Nem todas as integrações estão ativas por defeito.'
    },
    dataSecurity: {
      title: 'Segurança dos dados',
      text: 'Aplicamos medidas padrão da indústria: HTTPS, tokens de acesso, isolamento lógico por tenant, controlo por funções e armazenamento protegido de credenciais sensíveis. Nenhum método de transmissão ou armazenamento é 100 % infalível.'
    },
    retention: {
      title: 'Retenção de dados',
      text: 'Conservamos os seus dados enquanto a conta e o tenant estiverem ativos ou conforme exigido por lei. Pode solicitar eliminação da conta e dados associados contactando-nos pelos meios indicados abaixo.'
    },
    rights: {
      title: 'Os seus direitos',
      text: 'Pode aceder, corrigir ou solicitar eliminação dos seus dados pessoais. Os administradores da sua empresa gerem utilizadores e permissões no tenant. Para direitos sobre a conta individual, escreva para o email de contacto.'
    },
    changes: {
      title: 'Alterações a esta política',
      text: 'Podemos atualizar esta política periodicamente. Publicaremos a versão vigente nesta página e, em alterações relevantes, notificaremos utilizadores registados por email quando adequado.'
    },
    contact: {
      title: 'Contacto',
      text: 'Questões de privacidade: info@customer-lab.com.'
    }
  },
  terms: {
    title: 'Termos de Serviço',
    intro:
      'Estes Termos de Serviço («Termos») regulam a utilização do Customer Lab. Ao criar conta, aceitar convite ou usar a plataforma, aceita estes Termos.',
    acceptance: {
      title: 'Aceitação dos termos',
      text: 'Se não concordar com estes Termos, não pode utilizar o Customer Lab.'
    },
    description: {
      title: 'Descrição do serviço',
      text: 'O Customer Lab é uma plataforma operativa centrada em IA para clientes, documentos fiscais, comunicações, anexos e importações. O assistente consulta dados reais do tenant (ferramentas só de leitura quando aplicável) e ajuda a navegar a operação. A arquitetura está preparada para integrações de faturação eletrónica conforme disponibilidade; nem todas estão ativas no primeiro dia.'
    },
    accounts: {
      title: 'Contas e tenants',
      item1: 'Deve fornecer informação verídica ao registar-se ou completar um convite.',
      item2: 'É responsável pela segurança das credenciais e pelo uso da sua conta.',
      item3: 'Cada empresa opera num tenant isolado; os administradores gerem utilizadores e funções na organização.'
    },
    content: {
      title: 'Propriedade do conteúdo',
      text: 'A sua organização mantém a propriedade dos dados operacionais que carrega (clientes, faturas, comunicações, ficheiros). O Customer Lab não reivindica propriedade desse conteúdo; fornece ferramentas para o gerir e consultar.'
    },
    ai: {
      title: 'Assistente de IA',
      item1:
        'As respostas são geradas com serviços de IA de terceiros e dados do tenant. Não garantimos exatidão absoluta — reveja montantes, estados e IDs antes de decisões críticas.',
      item2:
        'O assistente foi concebido para citar registos reais e não inventar identificadores nem montantes; a revisão humana continua a ser sua responsabilidade.',
      item3: 'As ferramentas de consulta estão limitadas ao tenant e permissões; não acedemos a dados de outras empresas na plataforma.'
    },
    prohibited: {
      title: 'Uso proibido',
      item1: 'Utilizar a plataforma para atividades ilegais, fraudulentas ou que violem direitos de terceiros.',
      item2: 'Tentar contornar o isolamento entre tenants, explorar vulnerabilidades ou interferir com o serviço.',
      item3: 'Carregar malware, engenharia inversa não autorizada ou automação abusiva contra a API.',
      item4: 'Revender acesso ou partilhar credenciais fora dos utilizadores autorizados pelo administrador.'
    },
    termination: {
      title: 'Suspensão e cancelamento',
      text: 'Podemos suspender ou encerrar contas que violem estes Termos. A organização ou o utilizador podem solicitar o encerramento escrevendo para info@customer-lab.com.'
    },
    liability: {
      title: 'Limitação de responsabilidade',
      text: 'O Customer Lab é fornecido «tal como está». Não somos responsáveis por danos indiretos, perda de dados fora do nosso controlo razoável, erros em respostas de IA ou indisponibilidade de integrações de terceiros ainda não implementadas.'
    },
    changes: {
      title: 'Alterações a estes termos',
      text: 'Podemos atualizar estes Termos. A utilização continuada após publicação implica aceitação, salvo exigência legal em contrário.'
    },
    contact: {
      title: 'Contacto',
      text: 'Questões sobre estes Termos: info@customer-lab.com.'
    }
  }
};
